import React, { useState } from 'react';
import { CreditCard, Lock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  createCardToken,
  validateCardNumber,
  validateExpiry,
  validateSecurityCode,
  formatCardNumber,
  formatExpiry,
  expiryToYYMM,
  getCardBrand,
  type CardData,
  type PaymentProvider
} from '@/utils/cardTokenizer';
import { toast } from 'sonner';

interface CardRegistrationFormProps {
  onSuccess: (token: string) => void;
  onCancel?: () => void;
  loading?: boolean;
  paymentProvider?: PaymentProvider;
  /** GMO PG の場合に使用する Shop ID（API から取得） */
  gmoPgShopId?: string;
  /** 決済テストモード（true: テスト環境を使用） */
  testMode?: boolean;
}

const CardRegistrationForm: React.FC<CardRegistrationFormProps> = ({
  onSuccess,
  onCancel,
  loading: externalLoading = false,
  paymentProvider = 'fincode',
  gmoPgShopId,
  testMode = false
}) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  const [holderName, setHolderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    cardNumber?: string;
    expiry?: string;
    securityCode?: string;
  }>({});

  const cardBrand = getCardBrand(cardNumber);

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 19) {
      setCardNumber(formatted);
      setErrors(prev => ({ ...prev, cardNumber: undefined }));
    }
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatExpiry(e.target.value);
    if (formatted.replace(/\//g, '').length <= 4) {
      setExpiry(formatted);
      setErrors(prev => ({ ...prev, expiry: undefined }));
    }
  };

  const handleSecurityCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 4) {
      setSecurityCode(value);
      setErrors(prev => ({ ...prev, securityCode: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    // テストモード時は Luhn チェックをスキップ（GMO PG テストカードは Luhn を通らない場合がある）
    const cardNumberCleaned = cardNumber.replace(/\s/g, '');
    if (testMode) {
      // テストモード: 桁数チェックのみ（13-19桁）
      if (!/^\d{13,19}$/.test(cardNumberCleaned)) {
        newErrors.cardNumber = 'カード番号は13〜19桁の数字で入力してください';
      }
    } else if (!validateCardNumber(cardNumber)) {
      newErrors.cardNumber = 'カード番号が正しくありません';
    }

    if (!validateExpiry(expiry)) {
      newErrors.expiry = '有効期限が正しくありません';
    }

    if (!validateSecurityCode(securityCode)) {
      newErrors.securityCode = 'セキュリティコードが正しくありません';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const cardData: CardData = {
        cardNumber: cardNumber.replace(/\s/g, ''),
        expire: expiryToYYMM(expiry),
        securityCode,
        holderName: holderName ? holderName.toUpperCase() : undefined
      };

      const token = await createCardToken(paymentProvider, cardData, { gmoPgShopId, testMode });
      onSuccess(token);
    } catch (error: any) {
      console.error('Card tokenization error:', error);
      toast.error(error.message || 'カード情報の処理に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loading || externalLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* カード番号 */}
      <div className="space-y-2">
        <Label htmlFor="cardNumber">
          カード番号 <span className="text-red-500">*</span>
        </Label>
        <div className="relative">
          <Input
            id="cardNumber"
            type="text"
            placeholder="1234 5678 9012 3456"
            value={cardNumber}
            onChange={handleCardNumberChange}
            disabled={isLoading}
            className={errors.cardNumber ? 'border-red-500' : ''}
          />
          {cardBrand && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
              {cardBrand}
            </div>
          )}
        </div>
        {errors.cardNumber && (
          <div className="flex items-center gap-1 text-red-500 text-sm">
            <AlertCircle className="h-3 w-3" />
            {errors.cardNumber}
          </div>
        )}
      </div>

      {/* 有効期限とセキュリティコード */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="expiry">
            有効期限 <span className="text-red-500">*</span>
          </Label>
          <Input
            id="expiry"
            type="text"
            placeholder="MM/YY"
            value={expiry}
            onChange={handleExpiryChange}
            disabled={isLoading}
            className={errors.expiry ? 'border-red-500' : ''}
          />
          {errors.expiry && (
            <div className="flex items-center gap-1 text-red-500 text-sm">
              <AlertCircle className="h-3 w-3" />
              {errors.expiry}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="securityCode">
            セキュリティコード <span className="text-red-500">*</span>
          </Label>
          <Input
            id="securityCode"
            type="text"
            placeholder="123"
            value={securityCode}
            onChange={handleSecurityCodeChange}
            disabled={isLoading}
            className={errors.securityCode ? 'border-red-500' : ''}
          />
          {errors.securityCode && (
            <div className="flex items-center gap-1 text-red-500 text-sm">
              <AlertCircle className="h-3 w-3" />
              {errors.securityCode}
            </div>
          )}
        </div>
      </div>

      {/* カード名義人（任意） */}
      <div className="space-y-2">
        <Label htmlFor="holderName">
          カード名義人（任意）
        </Label>
        <Input
          id="holderName"
          type="text"
          placeholder="TARO YAMADA"
          value={holderName}
          onChange={(e) => setHolderName(e.target.value)}
          onBlur={(e) => setHolderName(e.target.value.toUpperCase())}
          disabled={isLoading}
          style={{ textTransform: 'uppercase' }}
        />
        <p className="text-xs text-gray-500">
          カードに記載されている名義を半角英字で入力してください
        </p>
      </div>

      {/* セキュリティ通知 */}
      <p className="text-xs text-gray-400 flex items-center gap-1">
        <Lock className="h-3 w-3" />
        カード情報は安全に暗号化され、当サイトのサーバーには保存されません
      </p>

      {/* ボタン */}
      <div className="flex gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1"
          >
            キャンセル
          </Button>
        )}
        <Button
          type="submit"
          disabled={isLoading}
          className="flex-1 bg-blue-500 hover:bg-blue-600"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              処理中...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              カードを登録
            </div>
          )}
        </Button>
      </div>
    </form>
  );
};

export default CardRegistrationForm;
